/**
 * 会话失效提示服务。
 *
 * 职责：
 * 1. 运行中 refresh 认证失败时统一提示登录已失效；
 * 2. 登录入口可用时提供重新登录动作；
 * 3. 登录入口缺失时提示配置 VITE_EXTERNAL_AUTH_URL。
 */

import type { DialogProviderInst, MessageProviderInst } from 'naive-ui';
import {
  externalAuthRedirectService,
  type ExternalAuthRedirectService,
} from '@/services/auth/ExternalAuthRedirectService';

/**
 * 会话失效提示服务依赖。
 */
export interface AuthSessionExpiryNoticeDeps {
  /**
   * 外部认证跳转服务。
   */
  redirectService: ExternalAuthRedirectService;

  /**
   * 获取当前页面地址。
   */
  getCurrentUrl: () => string;

  /**
   * 获取全局对话框实例。
   */
  getDialog: () => Pick<DialogProviderInst, 'warning'> | undefined;

  /**
   * 获取全局消息实例。
   */
  getMessage: () => Pick<MessageProviderInst, 'error'> | undefined;

  /**
   * 执行页面跳转。
   */
  redirectTo: (url: string) => void;
}

/**
 * 会话失效提示服务。
 */
export class AuthSessionExpiryNoticeService {
  /**
   * 会话失效提示依赖。
   */
  private readonly deps: AuthSessionExpiryNoticeDeps;

  /**
   * 是否已有会话失效提示正在展示。
   */
  private noticeVisible = false;

  /**
   * 创建会话失效提示服务。
   *
   * @param deps 会话失效提示依赖。
   */
  public constructor(deps: AuthSessionExpiryNoticeDeps) {
    this.deps = deps;
  }

  /**
   * 提示用户当前会话已失效。
   */
  public notifySessionExpired(): void {
    if (this.noticeVisible) {
      return;
    }

    this.noticeVisible = true;
    const resolution = this.deps.redirectService.resolveLoginRedirectUrl(this.deps.getCurrentUrl());

    if (resolution.status !== 'ready') {
      this.showMissingConfigNotice(resolution.message);
      return;
    }

    const dialog = this.deps.getDialog();
    if (dialog) {
      dialog.warning({
        title: '登录已失效',
        content: '当前登录状态已失效，请重新登录后继续使用。',
        positiveText: '重新登录',
        negativeText: '暂不跳转',
        maskClosable: false,
        onPositiveClick: () => {
          this.deps.redirectTo(resolution.url);
        },
        onNegativeClick: () => {
          this.noticeVisible = false;
        },
        onClose: () => {
          this.noticeVisible = false;
        },
      });
      return;
    }

    this.deps.getMessage()?.error('登录已失效，请重新登录。');
  }

  /**
   * 展示登录入口缺失或无效的提示。
   *
   * @param message 需要展示的错误说明。
   */
  private showMissingConfigNotice(message: string): void {
    const dialog = this.deps.getDialog();
    if (dialog) {
      dialog.warning({
        title: '登录地址未配置',
        content: message,
        positiveText: '我知道了',
        onPositiveClick: () => {
          this.noticeVisible = false;
        },
        onClose: () => {
          this.noticeVisible = false;
        },
      });
      return;
    }

    const messageApi = this.deps.getMessage();
    if (messageApi) {
      messageApi.error(message);
      return;
    }

    console.error(message);
  }
}

/**
 * 默认会话失效提示服务实例。
 */
export const authSessionExpiryNoticeService = new AuthSessionExpiryNoticeService({
  redirectService: externalAuthRedirectService,
  getCurrentUrl: () => (typeof window === 'undefined' ? '/' : window.location.href),
  getDialog: () => (typeof window === 'undefined' ? undefined : window.$dialog),
  getMessage: () => (typeof window === 'undefined' ? undefined : window.$message),
  redirectTo: (url) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.location.href = url;
  },
});
